import React, { useState, useEffect } from 'react';

// Hooks
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

// Actions
import { cancelOrder } from '~/redux/actions/orders-actions';

//Components
import ConfirmDialog from '~/components/Orders/ConfirmDialog/ConfirmDialog';

// Utils
import { dateFormat, doesItFallInCancellationPeriod, getTimeOnly } from '~/utils/date-format';
import { MESSAGES } from '~/utils/message';
import { createEnvelopeProof, createEnvelopeProofOrder, createViewProofForOrder } from '~/utils/template-builder';
import { success, failure } from '~/redux/actions/snackbar-actions';

// MUI Components
import { Grid, CircularProgress, Box, Button, Stepper, Step, StepLabel, StepConnector, stepConnectorClasses, Skeleton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

// Icons
import { CancelOrderCIcon, CancelOrderDetailsCIcon } from '~/assets/images';

// Styles
import './styles.scss';

// Custom MUI Stepper Components
const CustomConnector = styled(StepConnector)(() => ({
  '&.MuiStepConnector-alternativeLabel': {
    top: 8,
    left: 'calc(-50% + 8px)',
    right: 'calc(50% + 8px)',
  },
  '& .MuiStepConnector-line': {
    height: 2,
    border: 0,
    backgroundColor: '#e5e5e5',
    borderRadius: 1,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: '#ed5c2f',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: '#ed5c2f',
    },
  },
}));

const CustomStepIcon = styled('div')(({ ownerState }) => ({
  width: 16,
  height: 16,
  borderRadius: '50%',
  backgroundColor: ownerState.active || ownerState.completed ? '#ed5c2f' : '#e5e5e5',
  transition: 'all 0.3s ease',
}));

function CustomStepper({ info, cancellationWindow, refreshOverview, loading }) {
  const [activeStep, setActiveStep] = useState(0);
  const [proofLoading, setProofLoading] = useState(false);
  const [envelopeProofLoading, setEnvelopeProofLoading] = useState(false);
  const isCancellable = (info.status === 'Scheduled' && info.paymentStatus === 'Pending');

  const [dialog, setDialog] = useState({
    open: false,
    message: '',
    loading: false,
    currentAction: null,
    params: null,
  });

  const { id } = useParams();
  const dispatch = useDispatch();

  let steps = [
    {
      label: 'Order Date',
      date: `${dateFormat(info?.deliveryDate, true, true)}`,
    },
    {
      label: isCancellable && (<Typography sx={{
        color: '#000000',
        fontWeight: '600',
      
      }}>Cancellation Window</Typography>),
      date: (
        <>
          {isCancellable ? (
            <Box className='stepperCancelButtonContainer'>
              You can cancel your order before {getTimeOnly(cancellationWindow)} on {dateFormat(cancellationWindow, true)}
              <br />
              <Button className='stepperCancelButton' onClick={() => handleCancelOrder()}>
                Cancel Order
              </Button>
            </Box>
          ) : (
            <Box sx={{
              fontStyle: 'italic',
              color: '#00000099',
            }}>
              Cancelation closed {getTimeOnly(cancellationWindow)} on {dateFormat(cancellationWindow, true)}
            </Box>
          )}
        </>
      ),
    },
    {
      label: 'Processing Order',
      date: dateFormat(info.processingDate, true),
    },
    {
      label: 'Mailed',
      date: (
        info.mailedDate && (
        <Box className='stepperCancelButtonContainer'>
          Expected Mail Date 
            <br/>
            {dateFormat(info.mailedDate, true)}
          </Box>
        )
      )
    },
    {
      label: 'Delivered',
      date: (
        info.completedDate && (
        <Box className='stepperCancelButtonContainer'>
          Expected Delivery Date 
          <br/>
            {dateFormat(info.completedDate, true)}
          </Box>
        )
      )
    },
  ];

  const handleCloseDialog = () => {
    setDialog({ open: false, message: '', loading: false, params: null, currentAction: null });
  };

  const handleOk = async () => {
    setDialog((prev) => ({ ...prev, loading: true }));
    if (dialog.currentAction === 'cancel') {
      const payload = { orderId: id };
      const response = await dispatch(cancelOrder(payload));
      if (response.status === 200) {
        dispatch(success(response.data.message));
        refreshOverview();
      } else {
        dispatch(failure(response.data.message));
      }
    }
    setDialog((prev) => ({ ...prev, loading: false, open: false }));
  };

  const handleCancelOrder = async () => {
    setDialog({
      open: true,
      message: '',
      loading: false,
      currentAction: 'cancel',
      successButtonName: MESSAGES.ORDERS.DETAIL.MODALS.CANCEL.SUCCESS_BUTTON,
      subMessage: MESSAGES.ORDERS.DETAIL.MODALS.CANCEL.SUB_MESSAGE_NEW,
      heading: MESSAGES.ORDERS.DETAIL.MODALS.CANCEL.HEADING,
      icon: CancelOrderDetailsCIcon,
    });
  };

  const downloadViewProof = async () => {
    try {
      setProofLoading(true);

      await dispatch(
        createViewProofForOrder(`Order-${id}`, {
          orderId: id,
        })
      );
    } catch (error) {
      dispatch(failure(MESSAGES.VIEW_PROOF_ERROR));
    }
    setProofLoading(false);
  };

  const downloadEnvelopeProof = async () => {
    try {
      setEnvelopeProofLoading(true);
      let payload = {
        orderId: id,
      };
      await dispatch(createEnvelopeProof(`Order-${id}-Envelope-Proof`, payload));
    } catch (error) {
      dispatch(failure(MESSAGES.VIEW_PROOF_ERROR));
    } finally {
      setEnvelopeProofLoading(false);
    }
  };

  useEffect(() => {
    switch (info.status?.toUpperCase()) {
      case 'PROCESSING':
        setActiveStep(2);
        break;
      case 'MAILED':
        setActiveStep(3);
        break;
      case 'COMPLETED':
        setActiveStep(4);
        break;
      default:
        setActiveStep(0);
    }
  }, [info.status]);

  const CustomStepIconComponent = (props) => {
    const { active, completed } = props;
    return <CustomStepIcon ownerState={{ active, completed }} />;
  };

  if (loading) {
    return <Skeleton variant="rectangular" width="100%" height={50} />;
  }

  return (
    <Box className="stepper_wrapper" sx={{ width: '100%' }}>
      <Grid container spacing={2}>
        <Grid
          item
          lg={12}
          md={12}
          sm={12}
          xs={12}
        >
          <Box className="main_layer">
            <Stepper 
              activeStep={activeStep} 
              alternativeLabel 
              connector={<CustomConnector />}
              className={`custom-mui-stepper ${!isCancellable ? 'cancellation-window' : ''}`}
            >
              {steps.map((step, index) => (
                <Step key={step.label} completed={index < activeStep}>
                  <StepLabel
                    StepIconComponent={CustomStepIconComponent}
                  >
                    <Box className="step-content">
                      <Box className="step-text-top" sx={{
                        color: (index < activeStep || step.label === 'Order Date') ? '#000000' : '#545454',
                        fontWeight: (activeStep === index || index < activeStep) ? '600' : '400',
                      }}>{step.label}</Box>
                      <Box className="bottomtxt">{step.date}</Box>
                      {step.time && <Box className="bottomtxt">{step.time}</Box>}
                    </Box>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Grid>
      </Grid>
      {dialog.open && (
        <ConfirmDialog
          dialog={dialog}
          open={dialog.open}
          loading={dialog.loading}
          handleClose={handleCloseDialog}
          handleOk={handleOk}
        />
      )}
    </Box>
  );
}
export default CustomStepper;
